import React, { useEffect } from 'react';
import IconButton from '@material-ui/core/IconButton';
import ReactSVG from 'react-svg';
import { CircularProgress } from '@material-ui/core';
import * as lib from '~/dependencies';
import { EraseIcon, HideKeyboard } from '~/assets/svgIcons';
import * as NuacomComponent from '~/components';
import { formatNumber } from '~/helpers/format';
import { countryCodeAndFlagSelected } from '~/helpers/countryCodeAndFlagSelected';
import {
  Container,
  PaperContainer,
  InputBaseContainer,
  LoaderView,
} from './styles';

export default function NumberField(props) {
  const {
    value,
    changed,
    callAction,
    backSpace,
    cut,
    countryCode,
    setCountryCode,
    dtmfDismiss,
    cursor,
    isDtmf,
  } = props;

  // HOOKS
  const { t } = lib.useTranslation();

  // EFFECTS
  useEffect(() => {
    cursor(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (value && value.length > 4) {
      const convert = formatNumber(value, 'countryCode');
      const flagSelected = countryCodeAndFlagSelected(convert, 'callingCode');
      if (flagSelected) {
        const { callingCode, code } = flagSelected;
        setCountryCode({
          callingCode,
          code,
        });
        // Remove zero's and the plus sign
        let n = value.replace(/^(0||\+)+/, '');
        n = n.substring(callingCode.length);
        changed(n);
      }
    }
  }, [changed, setCountryCode, value]);

  function keyPressHandler(event) {
    if (event.key === 'Enter') {
      callAction();
    }
  }

  function pasteFromClipBoard(e) {
    e.preventDefault();
    const num = e.clipboardData.getData('Text');
    const numberFormated = String(num).replace(/[^0-9*#+]+/g, '');
    const convert = formatNumber(numberFormated, 'countryCode');

    const flagSelected = countryCodeAndFlagSelected(convert, 'callingCode');
    if (flagSelected) {
      const { callingCode, code } = flagSelected;
      setCountryCode({
        callingCode,
        code,
      });
    }

    if (convert) {
      if (numberFormated.startsWith('00')) {
        const newNumber = numberFormated.slice(2);
        if (newNumber.startsWith(convert)) {
          changed(newNumber.slice(convert.length));
        } else {
          changed(newNumber);
        }
      } else if (numberFormated.startsWith(convert)) {
        changed(numberFormated.slice(convert.length));
      } else {
        changed(numberFormated);
      }
    } else {
      changed(numberFormated);
    }
  }

  function handleChange(number) {
    let n = number;

    if (n.length > 4) {
      const convert = formatNumber(number, 'countryCode');
      const flagSelected = countryCodeAndFlagSelected(convert, 'callingCode');
      if (flagSelected) {
        const { callingCode, code } = flagSelected;
        // Remove zero's and the plus sign
        n = number.replace(/^(0||\+)+/, '');
        n = n.substring(callingCode.length);

        setCountryCode({
          callingCode,
          code,
        });
      }
    }
    changed(n);
  }

  function renderInputStartAdornment() {
    if (isDtmf) return null;

    if (countryCode?.code) {
      return (
        <NuacomComponent.FlagSelector
          countryCode={countryCode}
          setCountryCode={setCountryCode}
        />
      );
    }

    return (
      <LoaderView>
        <CircularProgress size={18} thickness={2} />
      </LoaderView>
    );
  }

  return (
    <Container>
      <PaperContainer>
        <div data-test-id="input-start-adornment">
          {renderInputStartAdornment()}
        </div>

        <InputBaseContainer
          placeholder={t('components.numberField.title')}
          inputProps={{ 'aria-label': 'Number' }}
          value={value || ''}
          onChange={text => handleChange(text.target.value)}
          onClick={e => cursor(e.target.selectionStart)}
          onCut={() => cut()}
          onKeyPress={keyPressHandler}
          autoFocus
          onPaste={pasteFromClipBoard}
        />

        {value.length > 0 && !isDtmf && (
          <IconButton
            aria-label="number"
            className="icon"
            onClick={() => backSpace()}>
            <ReactSVG src={EraseIcon} />
          </IconButton>
        )}

        {isDtmf && (
          <IconButton
            style={{ marginTop: 2 }}
            aria-label="hide dtmf input field"
            className="icon"
            onClick={() => dtmfDismiss()}>
            <ReactSVG src={HideKeyboard} />
          </IconButton>
        )}
      </PaperContainer>
    </Container>
  );
}

NumberField.defaultProps = {
  callAction: () => {},
  backSpace: () => {},
  cut: () => {},
  changed: () => {},
  setCountryCode: () => {},
  dtmfDismiss: () => {},
  countryCode: {},
  cursor: () => {},
  value: '',
  isDtmf: false,
};

NumberField.propTypes = {
  callAction: lib.PropTypes.func,
  backSpace: lib.PropTypes.func,
  cut: lib.PropTypes.func,
  changed: lib.PropTypes.func,
  cursor: lib.PropTypes.func,
  setCountryCode: lib.PropTypes.func,
  dtmfDismiss: lib.PropTypes.func,
  value: lib.PropTypes.string,
  countryCode: lib.PropTypes.instanceOf(Object),
  isDtmf: lib.PropTypes.bool,
};
